import { FC } from 'react';
import { useTranslation } from 'react-i18next';

import TeamPerfomanceCardSkeleton from './TeamPerfomanceCardSkeleton';

import { TrendDownIcon, TrendUpIcon } from '@/assets/icons';
import { Icon } from '@/components/atoms/Icon/Icon';
import { IconName } from '@/components/atoms/Icon/types';
import { formatEnpsScoreForDisplay } from '@/utils/enpsDisplay';
import { cn } from '@/utils/tailwind';

type TeamPerfomanceCardProps = {
  title?: string;
  icon?: IconName;
  /** Absent while no score is known yet; rendered as an em dash. */
  score?: number;
  comparedToAverage?: number;
  isLoading?: boolean;
  className?: string;
  size?: '1/3' | '2/3' | '3/3';
  showComparedToAverage?: boolean;
  scoreDisplay?: 'percentage' | 'enpsScore';
};

type ComparisonState = 'positive' | 'negative' | 'neutral';

const getComparisonState = (comparedToAverage: number | undefined): ComparisonState => {
  if (comparedToAverage === undefined) return 'neutral';
  if (comparedToAverage > 0) return 'positive';
  if (comparedToAverage < 0) return 'negative';
  return 'neutral';
};

export const TeamPerfomanceCard: FC<TeamPerfomanceCardProps> = ({
  title,
  icon,
  score,
  comparedToAverage,
  className,
  size = '2/3',
  isLoading,
  showComparedToAverage = true,
  scoreDisplay = 'percentage',
}) => {
  const { t } = useTranslation('components');

  if (isLoading) {
    return <TeamPerfomanceCardSkeleton />;
  }

  const scoreLabel =
    score === undefined
      ? '—'
      : scoreDisplay === 'enpsScore'
        ? formatEnpsScoreForDisplay(score)
        : `${score}%`;
  const comparisonState = getComparisonState(comparedToAverage);
  const deltaLabel =
    comparedToAverage === undefined
      ? null
      : scoreDisplay === 'enpsScore'
        ? formatEnpsScoreForDisplay(Math.abs(comparedToAverage))
        : `${Math.abs(comparedToAverage)}%`;

  const trendIcon =
    comparisonState === 'positive' ? (
      <TrendUpIcon width={19} height={19} />
    ) : comparisonState === 'negative' ? (
      <TrendDownIcon width={19} height={19} />
    ) : (
      <span className="text-body-20-semibold text-custom-warm-grey-500" aria-hidden>
        —
      </span>
    );

  const trendText =
    comparisonState === 'positive'
      ? t('teamPerformanceCard.comparedToAverage.more')
      : comparisonState === 'negative'
        ? t('teamPerformanceCard.comparedToAverage.less')
        : t('teamPerformanceCard.comparedToAverage.same');

  const scoreColorClass =
    !showComparedToAverage || comparisonState === 'neutral'
      ? 'text-custom-warm-grey-800'
      : comparisonState === 'positive'
        ? '!text-custom-emerald-700'
        : '!text-custom-red-700';

  const comparisonValueColorClass =
    comparisonState === 'positive'
      ? '!text-custom-emerald-700'
      : comparisonState === 'negative'
        ? '!text-custom-red-700'
        : '!text-custom-warm-grey-500';

  const renderComparisonRow = () => {
    if (!showComparedToAverage) {
      return null;
    }

    if (comparisonState === 'neutral') {
      return (
        <div className="flex items-center gap-1.5">
          <p className="text-body-14-regular !text-custom-warm-grey-500">{trendText}</p>
        </div>
      );
    }

    return (
      <div className="flex items-center gap-1.5">
        <p className={cn('text-body-14-semibold', comparisonValueColorClass)}>{deltaLabel}</p>
        <p className="text-body-14-regular !text-custom-warm-grey-700">{trendText}</p>
      </div>
    );
  };

  const renderScoreRow = () => (
    <div className={cn('text-body-20-semibold flex items-center gap-1.5', scoreColorClass)}>
      {scoreLabel}
      {showComparedToAverage ? trendIcon : null}
    </div>
  );

  const renderBySize = () => {
    switch (size) {
      case '1/3':
        return (
          <div className="flex flex-col gap-2">
            <div className="flex items-center gap-2">
              <div>
                {icon && (
                  <span className="text-custom-warm-grey-500">
                    <Icon name={icon} />
                  </span>
                )}
              </div>
              {title && (
                <p className="text-body-16-semibold !text-custom-warm-grey-700 truncate">{title}</p>
              )}
            </div>
            <div className="flex flex-col gap-1">
              {renderScoreRow()}
              {renderComparisonRow()}
            </div>
          </div>
        );
      case '2/3':
        return (
          <div className="flex flex-col gap-1">
            {renderScoreRow()}
            {renderComparisonRow()}
          </div>
        );
      case '3/3':
        return (
          <div className="flex flex-col gap-1">
            <div className="flex items-center gap-2">
              {title && (
                <span className="text-body-16-semibold !text-custom-warm-grey-700 truncate">
                  {title}
                </span>
              )}
            </div>
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <div className={cn('flex flex-col gap-[10px] max-w-full', className)}>{renderBySize()}</div>
  );
};
